"use client";

import posthog from "posthog-js";
import { firstTouchToPersonPropsOnce, readFirstTouch } from "./firstTouch";
import { validateEvent } from "./registry";
import { createTracker, type AnalyticsSink, type Track } from "./track";
import { EVENTS } from "./registry";
import { installClientExceptionDiagnostics } from "./clientDiagnostics";

/**
 * Browser sink. Shares the same `distinct_id` scheme as the server sink
 * (`resolveDistinctId` in envelope.ts) via `posthog.identify`, so a user's
 * client-side and server-side events land on one PostHog person. No-ops
 * (console.debug only) when NEXT_PUBLIC_POSTHOG_KEY isn't set.
 */

/** Browser-safe app-version resolver — reads the build-time-inlined NEXT_PUBLIC_APP_VERSION (see next.config.ts), no Node APIs. */
export function getClientAppVersion(): string {
  return process.env.NEXT_PUBLIC_APP_VERSION ?? "unknown";
}

let initialized = false;

/**
 * Initialize posthog-js once (idempotent). Returns false when there's no window
 * or no NEXT_PUBLIC_POSTHOG_KEY (SSR / unconfigured → analytics no-ops). Exported
 * so a client bootstrap can mount PostHog on the PUBLIC surfaces too — otherwise
 * posthog-js only ever loads when a custom event fires (the signed-in editor),
 * and content/map/marketing pages capture nothing.
 */
export function ensurePosthogInitialized(): boolean {
  if (typeof window === "undefined") return false;
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!apiKey) return false;
  if (!initialized) {
    // No hardcoded vendor host literal — see server.ts for the same policy.
    const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    posthog.init(apiKey, {
      ...(apiHost ? { api_host: apiHost } : {}),
      // MUST be the literal "history_change", not `true`. Verified against the
      // installed posthog-js source (1.396.4): capture_pageview:true only fires
      // the initial on-load pageview — it does NOT hook history.pushState, so
      // Next.js App Router <Link>/router.push soft navigations (which never
      // reload the page) would go uncaptured. Only "history_change" enables the
      // history-API listener that catches those too. capture_pageleave defaults
      // to "if_capture_pageview", so pageleave (session-duration accuracy) comes
      // along for free with either truthy value — no separate setting needed.
      capture_pageview: "history_change",
      // person_profiles: "identified_only" IS the library default (verified in
      // source) — set explicitly so it reads as a decision, not an oversight.
      // Anonymous organic pageviews stay cheap anonymous events; a person is
      // only created on sign-in, the right cost profile for a high-traffic
      // content site.
      person_profiles: "identified_only",
      // Opts into the SDK's current default bundle (masking/session-recording/
      // persistence improvements) instead of silently running on the oldest
      // backward-compatible baseline ("unset", what you get if this key is
      // omitted — verified in source: `defaults: null!=t?t:"unset"`). Pinned to
      // the latest version-gate shipped in posthog-js 1.396.4, like an API
      // version pin: bump deliberately after reviewing the changelog, not as a
      // silent side effect of a future `pnpm update`.
      defaults: "2026-06-25",
      // Real-user Web Vitals (FAIL-400 MP6/AC-13) — the only correct way to get
      // INP: it's defined by actual user interactions, so no headless/synthetic
      // check (scripts/seo/cwv-check.ts covers LCP/CLS/TTFB) can produce it.
      // Ships as $web_vitals events through the same capture endpoint already
      // allow-listed in CSP connect-src — no separate host.
      capture_performance: { web_vitals: true },
      // Browser exception autocapture (MAP-192): send a `$exception` event for
      // unhandled window errors AND unhandled promise rejections, so the errors
      // real users hit (React render crashes, async failures) reach PostHog error
      // tracking — the server `captureException` path only covers server code.
      // Console errors are deliberately left off (the default): they're noisy and
      // rarely actionable. NOTE: posthog-js loads the exception-autocapture logic
      // as a LAZY EXTERNAL chunk from the assets host (us-assets.i.posthog.com),
      // NOT from the bundle — so CSP script-src must admit that host or this fails
      // silently. buildSecurityHeaders() adds it (src/lib/security/csp.ts).
      capture_exceptions: true,
    });
    posthog.register({ app_version: getClientAppVersion() });
    // Railway-host traffic is production QA, not customer traffic. Mark the
    // person with PostHog's native test-user property so the project's existing
    // "Internal / Test users" cohort and filterTestAccounts setting exclude it.
    // Raw events remain available for debugging and deployment verification.
    if (window.location.hostname.endsWith(".up.railway.app")) {
      posthog.setInternalOrTestUser();
    }
    installClientExceptionDiagnostics((diagnostic) => {
      posthog.capture(EVENTS.CLIENT_EXCEPTION_DIAGNOSED, diagnostic);
    });
    initialized = true;
  }
  return true;
}

/**
 * Report a browser-origin error to PostHog error tracking (MAP-192). Used by the
 * root error boundary (app/global-error.tsx) for React render crashes, which the
 * `capture_exceptions` autocapture (window errors/rejections) can't see. Kept
 * HERE, behind the client's existing dynamic-import boundary, so global-error can
 * `import()` it lazily and posthog-js stays code-split OUT of the shared/embed
 * bundle (FAIL-448) — a static `import "posthog-js"` in global-error would pull
 * the whole library back into every route's bundle. No-ops when unconfigured.
 */
export function captureClientException(error: unknown): void {
  if (!ensurePosthogInitialized()) return;
  posthog.captureException(error instanceof Error ? error : new Error(String(error)));
}

export const clientAnalyticsSink: AnalyticsSink = {
  capture(eventName, envelope) {
    if (!ensurePosthogInitialized()) {
      console.debug("[analytics] no NEXT_PUBLIC_POSTHOG_KEY configured", eventName, envelope);
      return;
    }
    const { distinct_id, ...properties } = envelope;
    // $set_once (3rd arg) — PostHog's native durable first-touch mechanism:
    // written on the FIRST identify() for a person, never overwritten after,
    // so it survives regardless of which event happens to fire first post-signup.
    posthog.identify(distinct_id, undefined, firstTouchToPersonPropsOnce(readFirstTouch()));
    posthog.capture(eventName, properties);
  },
};

/**
 * The single browser-side tracker: the client sink + the schema-registry
 * validator, exactly like the server `track` in analytics/index.ts. Validation
 * is warn-only (analytics must never break the product flow that triggered it),
 * so a client event with a missing/renamed required prop surfaces in the console
 * + the registry test rather than throwing. Every client emitter builds its
 * funnel/paywall helpers on THIS so client-side taxonomy drift is caught too.
 */
export const clientTrack: Track = createTracker(clientAnalyticsSink, getClientAppVersion, {
  validate: validateEvent,
});
